// shared-lib/cabinet-runs/countertopAppearance.js
//
// The countertop half of "the scope is the RECORD, the object is a cache"
// (see reference-docs/cabinet-runs/overview.md).
//
// Cabinets have had a record since the appearance cascade: a re-emitted box is
// dressed from `override ?? run.appearance ?? room.cabinetAppearance`.
// Countertops never did — a slab's finish survived a re-solve only by being
// COPIED FORWARD off the incumbent (`preserveCountertopEdits`), matched by id,
// else by centre within 5 cm. Both keys move together when a run's START moves:
// a slab's id is keyed on wall + startParam, so the id changes, and the centre
// travels half the delta — well outside the window. Drag a run's left end and
// the finish, the edge profile and the waterfall end all go.
//
// So: a record, read as the FLOOR under the carry-forward. The incumbent still
// wins where there is one — a waterfall end chosen on one island slab is that
// slab's, not the room's — and a slab the fill rebuilt or invented has
// something to wear instead of nothing.
'use strict';

/**
 * The countertop cosmetics a SCOPE governs. A subset of
 * `COUNTERTOP_EDIT_FIELDS`: `endFinish` and `options` are deliberately absent
 * because they are per-slab answers, not room-wide ones — `styleCountertop`
 * derives `endFinish` from whether the slab is free-standing, and `options`
 * holds the styling layer's per-object overrides.
 */
const COUNTERTOP_APPEARANCE_FIELDS = [
  'texture',
  'solidColor',
  'solidColorFinish',
  'edgeProfile',
];

/**
 * The narrowest scope that has one wins — the same ladder as
 * `effectiveAppearance` for cabinets, one rung shorter: a countertop has no
 * unit override, because there is no bay to hang one on.
 *
 * @param {Object} [run]   the stored/hydrated run (`countertopAppearance`)
 * @param {Object} [room]  the room (`countertopAppearance`)
 * @returns {Object|null}
 */
function effectiveCountertopAppearance({ run, room } = {}) {
  return (
    (run && run.countertopAppearance) ||
    (room && room.countertopAppearance) ||
    null
  );
}

/**
 * Write a countertop appearance bundle's fields onto a built slab.
 *
 * Same null-vs-undefined contract as `applyAppearanceBundle`: an undefined key
 * is SKIPPED (the scope does not speak to it), a null is written (the scope
 * says that half of the finish is off — how a solid colour turns a texture
 * off, and the reverse). Firestore rejects undefined anywhere in a document,
 * so a writer must never put one in the record.
 */
function applyCountertopAppearance(countertop, bundle) {
  if (!bundle) return;
  for (const f of COUNTERTOP_APPEARANCE_FIELDS) {
    if (bundle[f] !== undefined) countertop[f] = bundle[f];
  }
}

module.exports = {
  COUNTERTOP_APPEARANCE_FIELDS,
  effectiveCountertopAppearance,
  applyCountertopAppearance,
};
